/* eslint-disable max-lines */
import {
    ChatAltIcon,
    ChevronRightIcon,
    ExternalLinkIcon,
    HeroEyeIcon,
    HeroEyeSlashIcon,
    HeroMicrophoneIcon,
    LogoutIcon,
    NinchatMicrophoneSlashIcon,
    StarIcon,
    StarIconSolid,
    UserAddIcon,
} from '../Icon'

import {
    UserInfoViewProps, UserRights,
} from './types'
import {
    actions,
    avatar,
    badgeTitle,
    badges,
    icon,
    info as infoStyles,
    userInfoViewWrapper, userName,
} from './styles/UserInfoView'
import Button from 'components/Button'
import {DropdownButtonNew} from 'components/Dropdown'
import DropdownNestedMenu from 'components/Dropdown/DropdownNestedMenu'
import DropdownNestedMenuContent from 'components/Dropdown/DropdownNestedMenuContent'
import {Icon} from 'components/Icon/types'
import {MessageActionTypes} from 'components/Messages/types'
import React from 'react'
import UserAvatar from '../Avatar/UserAvatar'

import {styled} from 'stitches/config'

const StyledUserName = styled('div', userName)
const StyledUserInfoViewWrapper = styled('div', userInfoViewWrapper)
const StyledAvatar = styled('div', avatar)
const StyledInfo = styled('div', infoStyles)
const StyledIcon = styled('span', icon)
const StyledBadges = styled('div', badges)
const StyledBadgeTitle = styled('div', badgeTitle)
const StyledActions = styled('div', actions)

const UserInfoView = (props: UserInfoViewProps): React.ReactElement => {
    const {
        autohide,
        canAddToOrganization,
        canChangeRights,
        canModerate,
        company,
        disableRemoveOperator,
        handleChannelAction,
        iconURL,
        jobTitle,
        light,
        loadDialogue,
        moderator,
        name,
        onAddToOrganization,
        operator,
        silenced,
        size,
        toggleRights,
        translations,
        userId,
        websiteUrl,
    } = props

    const showRightsActions = canChangeRights
    const showModerationActions = canModerate

    return (
        <StyledUserInfoViewWrapper light={light}>
            <StyledAvatar size={size}>
                <UserAvatar name={name} size={size} src={iconURL} />
            </StyledAvatar>
            <StyledUserName mid={size === 'mid'} title={name}>{name}</StyledUserName>
            {(!!jobTitle || !!company || !!websiteUrl) &&
                <StyledInfo>
                    {!!jobTitle &&
                        <div>
                            {jobTitle}
                        </div>}
                    {!!company &&
                        <div>
                            {company}
                        </div>}
                    {!!websiteUrl &&
                        <div>
                            <a
                                className='rowParent'
                                href={websiteUrl}
                                rel='noopener noreferrer'
                                target='_blank'
                                title={websiteUrl}
                            >
                                {websiteUrl}
                                <StyledIcon title={translations.externalLink}><ExternalLinkIcon /></StyledIcon>
                            </a>
                        </div>}
                </StyledInfo>}
            {(!!operator || !!moderator) &&
                <StyledBadges>
                    {!!operator &&
                        <div>
                            <StyledIcon badge={true}>
                                <StarIconSolid />
                            </StyledIcon>
                            <StyledBadgeTitle>{translations.operator}</StyledBadgeTitle>
                        </div>}
                    {!!moderator &&
                        <div>
                            <StyledIcon badge={true}>
                                <StarIcon />
                            </StyledIcon>
                            <StyledBadgeTitle>{translations.moderator}</StyledBadgeTitle>
                        </div>}
                </StyledBadges>}

            {/* eslint-disable-next-line no-negated-condition */}
            {size !== 'big' ?
                <StyledActions>
                    <Button
                        color='text'
                        iconLeft={ChatAltIcon}
                        onPress={() => loadDialogue(userId)}
                        text={translations.startPrivateConversation}
                        type='button'
                    />
                    {canAddToOrganization ?
                        <Button
                            color='text'
                            iconLeft={UserAddIcon}
                            onPress={onAddToOrganization}
                            text={translations.addToOrganization}
                            type='button'
                        />
                        : null}
                    {showRightsActions ?
                        <>
                            <Button
                                color='text'
                                disabled={disableRemoveOperator}
                                iconLeft={StarIconSolid}
                                onPress={() => toggleRights(UserRights.operator, !operator)}
                                text={operator ? translations.removeOperatorRights : translations.giveOperatorRights}
                                type='button'
                            />
                            <Button
                                color='text'
                                iconLeft={StarIcon}
                                onPress={() => toggleRights(UserRights.moderator, !moderator)}
                                text={moderator ? translations.removeModerationRights : translations.giveModerationRights}
                                type='button'
                            />
                        </>
                        : null}
                    {showModerationActions ?
                        <>
                            {silenced ?
                                <Button
                                    color='text'
                                    iconLeft={HeroMicrophoneIcon as unknown as Icon}
                                    onPress={handleChannelAction(MessageActionTypes.unsilence)}
                                    text={translations.unsilenceUser}
                                    type='button'
                                />
                                :
                                <DropdownNestedMenu>
                                    <DropdownButtonNew
                                        color='text'
                                        css={{
                                            '> span': {
                                                textAlign: 'left',
                                                width: '100%',
                                            },
                                            'width': '100%',
                                        }}
                                        iconLeft={NinchatMicrophoneSlashIcon as unknown as Icon}
                                        iconRight={ChevronRightIcon}
                                        text={`${translations.silenceUser}...`}
                                        type='button'
                                    />
                                    <DropdownNestedMenuContent direction='left'>
                                        <Button
                                            color='text'
                                            onPress={handleChannelAction(MessageActionTypes.silence)}
                                            text={translations.untilUnsilenced}
                                            type='button'
                                        />
                                        <Button
                                            color='text'
                                            onPress={handleChannelAction(MessageActionTypes.silence5m)}
                                            text={translations.for5Minutes}
                                            type='button'
                                        />
                                        <Button
                                            color='text'
                                            onPress={handleChannelAction(MessageActionTypes.silence15m)}
                                            text={translations.for15Minutes}
                                            type='button'
                                        />
                                        <Button
                                            color='text'
                                            onPress={handleChannelAction(MessageActionTypes.silence1h)}
                                            text={translations.for1Hour}
                                            type='button'
                                        />
                                        <Button
                                            color='text'
                                            onPress={handleChannelAction(MessageActionTypes.silence24h)}
                                            text={translations.for24Hours}
                                            type='button'
                                        />
                                    </DropdownNestedMenuContent>
                                </DropdownNestedMenu>}
                            <Button
                                color='text'
                                iconLeft={HeroEyeSlashIcon as unknown as Icon}
                                onPress={handleChannelAction(MessageActionTypes.hideAllMessages)}
                                text={translations.hideAllMessages}
                            />
                            {autohide ?
                                <Button
                                    color='text'
                                    iconLeft={HeroEyeIcon as unknown as Icon}
                                    onPress={handleChannelAction(MessageActionTypes.showNewMessages)}
                                    text={translations.showNewMessages}
                                />
                                :
                                <Button
                                    color='text'
                                    iconLeft={HeroEyeSlashIcon as unknown as Icon}
                                    onPress={handleChannelAction(MessageActionTypes.hideNewMessages)}
                                    text={translations.hideNewMessages}
                                />}
                            <Button color='text' iconLeft={LogoutIcon} onPress={handleChannelAction(MessageActionTypes.removeUser)} text={translations.removeUserFromChannel} />
                        </>
                        : null}
                </StyledActions>
                : null}
        </StyledUserInfoViewWrapper>
    )
}

export default UserInfoView
